import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  TextField,
} from '@mui/material';
import { useDebouncedValue } from '../../docs/utils/useDebouncedValue';
import { getErrorMessage } from '../../docs/utils/docsUtils';
import { useCreateTrackedFeature, useFeatureCatalog } from '../hooks';
import type { CatalogItem, FeatureKind } from '../types';

interface AddTrackedFeatureDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (name: string) => void;
}

const SEARCH_DEBOUNCE_MS = 300;
const GROUP_PATTERN = /^[a-z][a-z0-9_]{1,63}$/;

const text = {
  cancel: 'Cancel',
  empty: 'Nothing left to track for this search.',
  group: 'Group code',
  groupHelp: 'snake_case, e.g. media. Features in a group can be summed in the chart.',
  kinds: { all: 'All', route: 'REST route', event: 'Event', tool: 'LLM tool' },
  name: 'Display name',
  save: 'Start tracking',
  search: 'Search routes, events or tools',
  title: 'Add a service to track',
} as const;

export const AddTrackedFeatureDialog = ({
  open,
  onClose,
  onCreated,
}: AddTrackedFeatureDialogProps) => {
  const [kind, setKind] = useState<FeatureKind | ''>('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<CatalogItem | null>(null);
  const [name, setName] = useState('');
  const [groupCode, setGroupCode] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS);
  const catalog = useFeatureCatalog(kind || undefined, debouncedSearch, open);
  const create = useCreateTrackedFeature();

  useEffect(() => {
    if (!open) return;
    setKind('');
    setSearch('');
    setSelected(null);
    setName('');
    setGroupCode('');
    create.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const select = (item: CatalogItem) => {
    setSelected(item);
    setName(item.label);
  };

  const canSave = Boolean(selected && name.trim() && GROUP_PATTERN.test(groupCode));

  const save = () => {
    if (!selected || !canSave) return;
    create.mutate(
      { kind: selected.kind, matchKey: selected.matchKey, name: name.trim(), groupCode },
      {
        onSuccess: () => {
          onCreated(name.trim());
          onClose();
        },
      }
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{text.title}</DialogTitle>
      <DialogContent className="flex flex-col gap-3 !pt-2">
        <div className="flex flex-col gap-3 sm:flex-row">
          <TextField
            select
            size="small"
            label="Type"
            value={kind}
            onChange={(event) => setKind(event.target.value as FeatureKind | '')}
            className="sm:w-40"
          >
            <MenuItem value="">{text.kinds.all}</MenuItem>
            <MenuItem value="route">{text.kinds.route}</MenuItem>
            <MenuItem value="event">{text.kinds.event}</MenuItem>
            <MenuItem value="tool">{text.kinds.tool}</MenuItem>
          </TextField>
          <TextField
            size="small"
            label={text.search}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            fullWidth
          />
        </div>

        <div className="max-h-60 overflow-y-auto rounded-lg border border-gray-200 dark:border-slate-700">
          {catalog.isLoading ? (
            <div className="flex justify-center p-4">
              <CircularProgress size={22} />
            </div>
          ) : catalog.data?.items.length ? (
            <List dense disablePadding>
              {catalog.data.items.map((item) => (
                <ListItemButton
                  key={`${item.kind}:${item.matchKey}`}
                  selected={selected?.matchKey === item.matchKey && selected.kind === item.kind}
                  onClick={() => select(item)}
                >
                  <ListItemText
                    primary={item.label}
                    secondary={`${item.kind} · ${item.matchKey}`}
                    secondaryTypographyProps={{ className: 'break-all' }}
                  />
                </ListItemButton>
              ))}
            </List>
          ) : (
            <p className="p-4 text-center text-sm text-gray-500 dark:text-slate-400">
              {text.empty}
            </p>
          )}
        </div>

        <TextField
          size="small"
          label={text.name}
          value={name}
          onChange={(event) => setName(event.target.value)}
          disabled={!selected}
          inputProps={{ maxLength: 100 }}
        />
        <TextField
          size="small"
          label={text.group}
          value={groupCode}
          onChange={(event) => setGroupCode(event.target.value.toLowerCase())}
          disabled={!selected}
          error={Boolean(groupCode) && !GROUP_PATTERN.test(groupCode)}
          helperText={text.groupHelp}
        />
        {create.isError && <Alert severity="error">{getErrorMessage(create.error)}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{text.cancel}</Button>
        <Button variant="contained" onClick={save} disabled={!canSave || create.isPending}>
          {text.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
